import { Stack, Typography } from '@mui/material';
import { MarketingCampaignWithSource } from '../../../../types/marketing';
import { campaignHasEmailStatistics } from '../../../../helpers/marketing';
import { formatDollarAmount } from '../../../../utils/global/helper';
import useGetCampaignMetrics from '../../../../hooks/marketing/useGetCampaignMetrics';
import CampaignStatistic from '../CampaignStatistic';

interface Props {
  campaign: MarketingCampaignWithSource;
}

function CampaignAnalyticsCell({ campaign }: Props) {
  const { source } = campaign;
  const { statistics, status } = source;
  const isDraft = status === 'draft';

  const { data: campaignMetricsData } = useGetCampaignMetrics(
    !isDraft ? campaign.id : null,
  );

  const hasEmailStats = campaignHasEmailStatistics(campaign);

  const campaignMetrics = campaignMetricsData?.metrics;
  const campaignCost = campaignMetricsData?.campaignCost ?? 0;
  const mediumKeys = campaignMetrics ? Object.keys(campaignMetrics) : [];
  const roiTotals =
    campaignMetrics && mediumKeys.length > 0
      ? mediumKeys.reduce(
          (acc, key) => ({
            totalClicks: acc.totalClicks + campaignMetrics[key].totalClicks,
            bookingRequests:
              acc.bookingRequests + campaignMetrics[key].bookingRequests,
            totalRevenue: acc.totalRevenue + campaignMetrics[key].totalRevenue,
          }),
          { totalClicks: 0, bookingRequests: 0, totalRevenue: 0 },
        )
      : null;

  if (isDraft) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const roiStats = roiTotals && (
    <Stack flexDirection="row" gap={1.5}>
      <Stack alignItems="center">
        <Typography variant="h5">{roiTotals.totalClicks}</Typography>
        <Typography variant="body2">Clicks</Typography>
      </Stack>
      <Stack alignItems="center">
        <Typography variant="h5">{roiTotals.bookingRequests}</Typography>
        <Typography variant="body2">Requests</Typography>
      </Stack>
      <Stack alignItems="center">
        <Typography variant="h5">
          {formatDollarAmount(roiTotals.totalRevenue / 100)}
        </Typography>
        <Typography variant="body2">Revenue</Typography>
      </Stack>
      {campaignCost > 0 && (
        <Stack alignItems="center">
          <Typography variant="h5">
            {((roiTotals.totalRevenue / campaignCost) * 100).toFixed(0)}%
          </Typography>
          <Typography variant="body2">ROI</Typography>
        </Stack>
      )}
    </Stack>
  );

  if (hasEmailStats) {
    return (
      <Stack gap={0.5}>
        <Stack flexDirection="row" gap={1.5}>
          <CampaignStatistic statistics={statistics} type="opened" />
          <CampaignStatistic statistics={statistics} type="bounced" />
        </Stack>
        {roiStats}
      </Stack>
    );
  }

  // Text-only or other non-email campaigns
  return (
    roiStats || (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    )
  );
}

export default CampaignAnalyticsCell;
